import { el, mount, unmount, setAttr } from 'redom';

import { BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

import { stringToSVG } from 'utilities';

import BorderInputPanel from './border-input-panel';
import ColorPickerPanel from './color-picker-panel';
import ShadowInputPanel from './shadow-input-panel';
import PathInputPanel from './path-input-panel';
import PositionControlPanel from './position-control-panel';
import DuplicateObject from './duplicate-object';

import './index.scss';

import ToolbarBackgroundColorIconString from 'assets/images/text-background-color-icon.svg?raw';
import BorderIconString from 'assets/images/border-controls-icon.svg?raw';
import boxShadowIconString from 'assets/images/box-shadow-icon.svg?raw';
import pathInputIconString from 'assets/images/path-input-icon.svg?raw';

const pathInputIcon = stringToSVG(pathInputIconString);
const boxShadowIcon = stringToSVG(boxShadowIconString);
const BorderIcon = stringToSVG(BorderIconString);
const ToolbarBackgroundColorIcon = stringToSVG(ToolbarBackgroundColorIconString);

setAttr(BorderIcon, {
  class: 'border-icon'
});

class CanvasShapeToolbar {

  constructor(configuration) {

    this.configuration = configuration;
    this.state$ = new BehaviorSubject({
      displayBackgroundColorPicker: false,
      displayBorderInputPanel: false,
      displayShadowInputPanel: false,
      displayPathInputPanel: false
    });

    this.displayBackgroundColorPicker$ = this.state$.pipe(map(state => state.displayBackgroundColorPicker), distinctUntilChanged());
    this.displayBorderInputPanel$ = this.state$.pipe(map(state => state.displayBorderInputPanel), distinctUntilChanged());
    this.displayShadowInputPanel$ = this.state$.pipe(map(state => state.displayShadowInputPanel), distinctUntilChanged());
    this.displayPathInputPanel$ = this.state$.pipe(map(state => state.displayPathInputPanel), distinctUntilChanged());

    this.dynamicContentContainer = el('.editor-toolbar-dynamic-content-container');
    this.backgroundColorButton = null;
    this.pathInputButton = null;
    this.backgroundColorPicker = null;
    this.borderInputPanel = null;
    this.shadowInputPanel = null;
    this.pathInputPanel = null;

    this.el = el('.canvas-text-toolbar-main', [
      this.renderToolbar(),
      this.renderDynamicContent()
    ]);

    this.displayBackgroundColorPicker$.subscribe(this.handleBackgroundColorPickerDisplayChange.bind(this));
    this.displayBorderInputPanel$.subscribe(this.handleBorderInputPanelDisplayChange.bind(this));
    this.displayShadowInputPanel$.subscribe(this.handleShadowInputPanelDisplayChange.bind(this));
    this.displayPathInputPanel$.subscribe(this.handlePathInputPanelDisplayChange.bind(this));

    // this.displayBackgroundColorPicker$.subscribe(this.toggleActiveStatus.bind(this));
    // this.displayBorderInputPanel$.subscribe(this.toggleActiveStatus.bind(this));
    // this.displayShadowInputPanel$.subscribe(this.toggleActiveStatus.bind(this));
    // this.displayPathInputPanel$.subscribe(this.toggleActiveStatus.bind(this));

  }

  toggleActiveStatus(isVisible) {

    if (isVisible === true) {
      this.configuration.onActive();
    } else {
      this.configuration.onInactive();
    }

  }

  updateState(newState) {
    this.state$.next({ ...this.state$.value, ...newState });
  }

  handlPathInputPanelChange(path) {

    this.configuration.path = path;

    if (typeof this.configuration.callbacks.onPathChange === 'function') {
      this.configuration.callbacks.onPathChange(path);
    }

  }

  handlePathInputPanelDisplayChange(isVisible) {

    this.toggleActiveStatus(isVisible);
    if (isVisible === true) {
      this.pathInputPanel = new PathInputPanel({
        anchor: this.pathInputButton,
        value: this.configuration.path,
        onChange: (path) => this.handlPathInputPanelChange(path),
        onClose: () => this.updateState({
          displayPathInputPanel: !this.state$.value.displayPathInputPanel
        })
      });
      mount(this.dynamicContentContainer, this.pathInputPanel);
    } else if (this.pathInputPanel !== null) {
      unmount(this.dynamicContentContainer, this.pathInputPanel);
      this.pathInputPanel = null;
    }

  }

  handleShadowChange(shadow) {

    this.configuration.shadow = shadow;

    if (typeof this.configuration.callbacks.onShadowChange === 'function') {
      this.configuration.callbacks.onShadowChange(shadow);
    }

  }

  handleShadowInputPanelDisplayChange(isVisible) {

    this.toggleActiveStatus(isVisible);
    if (isVisible === true) {
      this.shadowInputPanel = new ShadowInputPanel({
        value: this.configuration.shadow,
        onChange: (shadow) => this.handleShadowChange(shadow),
        onClose: () => this.updateState({
          displayShadowInputPanel: !this.state$.value.displayShadowInputPanel
        })
      });
      mount(this.dynamicContentContainer, this.shadowInputPanel);
    } else if (this.shadowInputPanel !== null) {
      unmount(this.dynamicContentContainer, this.shadowInputPanel);
      this.shadowInputPanel = null;
    }

  }

  handleBackgroundColorChange(color) {

    this.configuration.color.background = color;

    if (typeof this.configuration.callbacks.onBackgroundColorChange === 'function') {
      this.configuration.callbacks.onBackgroundColorChange(color);
    }

  }

  handleBackgroundColorPickerDisplayChange(isVisible) {

    this.toggleActiveStatus(isVisible);
    if (isVisible === true) {
      this.backgroundColorPicker = new ColorPickerPanel({
        anchor: this.backgroundColorButton,
        color: this.configuration.color.background,
        onChange: (color) => this.handleBackgroundColorChange(color),
        onClose: () => this.updateState({
          displayBackgroundColorPicker:
            !this.state$.value.displayBackgroundColorPicker
        }),
        position: 'top',
        autoPosition: true
      });
      mount(this.dynamicContentContainer, this.backgroundColorPicker);
    } else if (this.backgroundColorPicker !== null) {
      unmount(this.dynamicContentContainer, this.backgroundColorPicker);
      this.backgroundColorPicker = null;
    }

  }

  handleBorderChange(border) {

    this.configuration.border = border;

    if (typeof this.configuration.callbacks.onBorderChange === 'function') {
      this.configuration.callbacks.onBorderChange(border);
    }

  }

  handleBorderInputPanelDisplayChange(isVisible) {

    this.toggleActiveStatus(isVisible);
    if (isVisible === true) {
      this.borderInputPanel = new BorderInputPanel({
        value: this.configuration.border,
        onChange: (border) => this.handleBorderChange(border),
        onClose: () => this.updateState({
          displayBorderInputPanel: !this.state$.value.displayBorderInputPanel
        })
      });
      mount(this.dynamicContentContainer, this.borderInputPanel);
    } else if (this.borderInputPanel !== null) {
      unmount(this.dynamicContentContainer, this.borderInputPanel);
      this.borderInputPanel = null;
    }

  }

  renderColorButtons() {

    this.backgroundColorButton = el('button.inline-toolbar-button.color-button', {
      onclick: () => this.updateState({
        displayBackgroundColorPicker:
          !this.state$.value.displayBackgroundColorPicker
      })
    }, [ToolbarBackgroundColorIcon.cloneNode(true)]);

    const buttons = [
      this.backgroundColorButton,
      el('button.inline-toolbar-button.border-button', {
        onclick: () => this.updateState({
          displayBorderInputPanel:
            !this.state$.value.displayBorderInputPanel
        })
      }, [BorderIcon.cloneNode(true)]),
      el('button.inline-toolbar-button.shadow-button', {
        onclick: () => this.updateState({
          displayShadowInputPanel:
            !this.state$.value.displayShadowInputPanel
        })
      }, [boxShadowIcon.cloneNode(true)])
    ];

    if (this.configuration.shapeType.toLowerCase() === 'path') {

      this.pathInputButton = el('button.inline-toolbar-button.path-button', {
        onclick: () => this.updateState({
          displayPathInputPanel:
            !this.state$.value.displayPathInputPanel
        })
      }, pathInputIcon.cloneNode(true));

      buttons.unshift(this.pathInputButton);
    }

    return buttons;

  }

  renderColorButtonsSection() {

    return el('.toolbar-section.color-buttons-section', [
      this.renderColorButtons()
    ]);

  }

  renderPositionControlPanel() {

    return new PositionControlPanel(this.configuration.callbacks);

  }

  renderToolbar() {

    return el('.toolbar-buttons-container editor-tool-container', [
      this.renderColorButtonsSection(),
      this.renderPositionControlPanel(),
      el('.toolbar-section', [
        new DuplicateObject(this.configuration)
      ])
    ]);

  }

  renderDynamicContent() {

    return this.dynamicContentContainer;

  }

}

export default CanvasShapeToolbar;